import { Component, EventEmitter, inject, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { DevolutionProjectionDetailModel } from 'src/app/models/preliminaries/devolution-projection-detail';
import { DevolutionProjectionDetailService } from 'src/app/services/preliminaries/devolution-projection-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-devolution-projection-detail',
  templateUrl: './register-devolution-projection-detail.component.html',
  styleUrls: ['./register-devolution-projection-detail.component.scss'],
  standalone: false
})
export class RegisterDevolutionProjectionDetailComponent implements OnInit {

  private readonly devolutionProjectionDetailService = inject(DevolutionProjectionDetailService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @Input() devolutionProjectionDetail: DevolutionProjectionDetailModel = new DevolutionProjectionDetailModel();
  @Input() devolutionProjectionDetails: DevolutionProjectionDetailModel[] = [];
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  formDevolutionProjectionDetail: FormGroup = new FormGroup({});
  devolutionProjectionDetailId: number = 0;
  codeNameArticle: string = '';
  codeNameStore: string = '';

  ngOnInit() {
    this.validateForm(new DevolutionProjectionDetailModel());
  }

  async ngOnChanges(changes: SimpleChanges) {
    const detailsChange = changes['devolutionProjectionDetails'];
    if (detailsChange && !detailsChange.firstChange) {
      const details: DevolutionProjectionDetailModel[] = this.devolutionProjectionDetails;
      await this.createDevolutionProjectionDetails(details);
      return;
    }
    const detailChange = changes['devolutionProjectionDetail'];
    if (!detailChange || detailChange.firstChange) {
      return;
    }
    const detail: DevolutionProjectionDetailModel = this.devolutionProjectionDetail;
    this.devolutionProjectionDetailId = detail.devolutionProjectionDetailId;
    this.validateForm(detail);

    if (detail.articleId > 0 && detail.storeId > 0) {
      await this.getArticleAndStoreNames();
    }
  }

  async clearForm() {
    this.isFormView.emit(false);
    this.codeNameStore = '';
    this.codeNameArticle = '';
    this.devolutionProjectionDetailId = 0;
    this.devolutionProjectionDetail = new DevolutionProjectionDetailModel();
    this.validateForm(this.devolutionProjectionDetail);
  }

  async createDevolutionProjectionDetails(devolutionProjectionDetails: DevolutionProjectionDetailModel[]) {
    try {
      await firstValueFrom(this.devolutionProjectionDetailService.createDevolutionProjectionDetails(devolutionProjectionDetails));
      this.toastHandlerService.showToast(
        'success',
        'Los detalles de la proyección de devolución fueron creados exitosamente.'
      );
      this.updateListDetail.emit(true);
    } catch (error) {
      console.error('Error al crear los detalles de la proyección de devolución de forma multiple.', error);
    }
  }

  async createDevolutionProjectionDetail(model: DevolutionProjectionDetailModel) {
    try {
      await firstValueFrom(this.devolutionProjectionDetailService.createDevolutionProjectionDetail(model));
      this.updateListDetail.emit(true);
      this.devolutionProjectionDetail = new DevolutionProjectionDetailModel();
      this.validateForm(this.devolutionProjectionDetail);
      this.toastHandlerService.showToast(
        'success',
        'El detalle de la proyección de devolución fue creado exitosamente.'
      );
    } catch (error) {
      console.error('Error al crear el detalle.', error);
    }
  }

  async getArticleAndStoreNames() {
    const detail: DevolutionProjectionDetailModel = this.devolutionProjectionDetail;
    try {
      const response = await firstValueFrom(
        this.devolutionProjectionDetailService.getArticleAndStoreNames(detail.articleId, detail.storeId)
      );

      this.codeNameArticle = '';
      if (response.article) {
        this.codeNameArticle = response.article;
      }

      this.codeNameStore = '';
      if (response.store) {
        this.codeNameStore = response.store;
      }
    } catch (error) {
      console.error('Error al obtener artículo y bodega.', error);
    }
  }

  async send() {
    this.validateFormControls();
    const form = this.formDevolutionProjectionDetail;
    if (form.invalid) {
      this.showValidationMessagesDetail();
      return;
    }
    const model = form.getRawValue() as DevolutionProjectionDetailModel;
    if (!this.validateQuantity(model.quantity)) {
      return;
    }
    this.isFormView.emit(false);
    if (this.devolutionProjectionDetailId == 0) {
      await this.createDevolutionProjectionDetail(model);
    } else {
      await this.updateDevolutionProjectionDetail(model);
    }
  }

  showValidationMessagesDetail() {
    const controlMessages: Record<string, Record<string, string>> = {
      devolutionProjectionId: {
        required: 'El campo Proyección de Devolución es obligatorio.',
        min: 'Debes seleccionar una Proyección de Devolución válida.'
      },
      articleId: {
        required: 'El campo Artículo es obligatorio.',
        min: 'Debes seleccionar un Artículo válido.'
      },
      storeId: {
        required: 'El campo Bodega es obligatorio.',
        min: 'Debes seleccionar una Bodega válida.'
      },
      quantity: {
        required: 'El campo Cantidad es obligatorio.',
        min: 'La cantidad debe ser mayor a cero.'
      }
    };
    Object.keys(controlMessages).forEach(control => {
      const formControl = this.formDevolutionProjectionDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  async updateDevolutionProjectionDetail(model: DevolutionProjectionDetailModel) {
    this.devolutionProjectionDetail.quantity = model.quantity;
    try {
      await firstValueFrom(
        this.devolutionProjectionDetailService.updateDevolutionProjectionDetail(this.devolutionProjectionDetail)
      );
      this.updateListDetail.emit(true);
      this.devolutionProjectionDetail = new DevolutionProjectionDetailModel();
      this.validateForm(this.devolutionProjectionDetail);
      this.toastHandlerService.showToast(
        'success',
        'El detalle de la proyección de devolución fue modificado exitosamente.'
      );
    } catch (error) {
      console.error('Error al modificar el detalle.', error);
    }
  }

  validateQuantity(quantity: number) {
    const stock = this.formDevolutionProjectionDetail.get('stock')?.value || 0;
    if (quantity > stock) {
      this.toastHandlerService.showToast(
        'error',
        'No puede ingresar una cantidad mayor a la cantidad de Existencia.'
      );
      return false;
    }
    return true;
  }

  async validateForm(devolutionProjectionDetail: DevolutionProjectionDetailModel) {
    this.formDevolutionProjectionDetail = this.formBuilder.group({
      devolutionProjectionId: [devolutionProjectionDetail.devolutionProjectionId, [Validators.required, Validators.min(1)]],
      articleId: [devolutionProjectionDetail.articleId, [Validators.required, Validators.min(1)]],
      storeId: [devolutionProjectionDetail.storeId, [Validators.required, Validators.min(1)]],
      quantity: [devolutionProjectionDetail.quantity, [Validators.required, Validators.min(1)]],
      stock: [{ value: devolutionProjectionDetail.stock, disabled: true }]
    });
  }

  validateFormControls() {
    Object.keys(this.formDevolutionProjectionDetail.controls).forEach((key) => {
      const control = this.formDevolutionProjectionDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
